<script setup lang="ts">
/**
 * Section 共享壳 —— 结构视觉对齐 `project/index.html` `.scu-widget`
 * （2026-06-25 grill 第四轮 R1/R6 决策：模板结构视觉近 1:1）。
 *
 * DOM 语义对照：
 *   `.scu-widget`        → 根 `<section>`（`data-shell="scu-widget"`）
 *   `.scu-title-wrapper` → 标题条（底边线 + flex space-between）
 *   `.scu-title-tab`     → 红底白字斜切 tab（含可选 `title-icon` slot）
 *   `.widget-inner`      → 内容区（`data-shell="scu-widget-inner"`）
 *
 * **`more` slot**：标题条右侧「更多+」入口接缝，由 F12 接 router-link
 * 跳 `/detail/:sectionId/:slug?`（Q15）；F05 不填充，留空即不渲染。
 * **`title-icon` slot**：标题 tab 内的固定模板图标（titico.png）接缝，
 * 由 chrome/template alignment issue（R7）从 `public/template/` 注入。
 *
 * 常规 Section 组件根级不写跨列强约束（Q7）：PC 端占列由 HomePage
 * 主容器 `auto-fit minmax` 决定，全宽 type（banner / research-institutions）
 * 由各自 issue 在组件根级自带跨列样式（`1 / -1` 全宽）。
 */
import { computed, useSlots } from 'vue';

import DraftBadge from '@/components/DraftBadge.vue';
import { hasText } from './section-presenters';

const props = defineProps<{
  readonly id?: string;
  readonly type: string;
  readonly title?: string | null;
  readonly summary?: string | null;
  /** 预览模式下 Draft Section 挂草稿角标（F08，由 useDraftBadge 计算后传入）。 */
  readonly showDraftBadge?: boolean;
}>();

const slots = useSlots();
const hasTitle = computed(() => hasText(props.title));
const hasSummary = computed(() => hasText(props.summary));
const showHeader = computed(
  () => hasTitle.value || hasSummary.value || Boolean(slots.more),
);
</script>

<template>
  <section
    :id="props.id"
    :class="$style.widget"
    :data-section-type="props.type"
    data-shell="scu-widget"
  >
    <DraftBadge v-if="props.showDraftBadge" :class="$style.draftBadge" />
    <div
      v-if="showHeader"
      :class="$style.titleWrapper"
      data-shell="scu-title-wrapper"
    >
      <div v-if="hasTitle" :class="$style.titleTab" data-shell="scu-title-tab">
        <img
          :class="$style.titleIcon"
          src="/template/titico.png"
          alt=""
          aria-hidden="true"
        >
        <slot name="title-icon" />
        <span>{{ props.title }}</span>
      </div>
      <slot name="more" />
    </div>
    <div :class="$style.inner" data-shell="scu-widget-inner">
      <p v-if="hasSummary" :class="$style.summary">{{ props.summary }}</p>
      <slot />
    </div>
  </section>
</template>

<style module>
.widget {
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--color-bg-primary);
  padding: 0 0 var(--template-widget-padding-bottom);
  box-shadow: var(--shadow-card);
}

.draftBadge {
  position: absolute;
  top: var(--space-2);
  right: var(--space-3);
  z-index: 1;
}

.titleWrapper {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  border-bottom: 1px solid var(--color-border-strong);
  margin-bottom: var(--template-title-wrapper-margin-bottom);
  padding-right: var(--template-title-wrapper-padding-right);
}

.titleTab {
  display: inline-flex;
  align-items: center;
  background: var(--color-brand-primary);
  color: var(--color-bg-primary);
  padding: var(--template-title-tab-padding-y) var(--template-title-tab-padding-right)
    var(--template-title-tab-padding-y) var(--template-title-tab-padding-left);
  font-size: var(--font-size-lg);
  font-weight: 700;
  /* 模板斜切右边（slant 深度走 --template-title-tab-slant）。 */
  clip-path: polygon(0 0, 100% 0, calc(100% - var(--template-title-tab-slant)) 100%, 0 100%);
}

.titleIcon {
  height: var(--template-title-icon-height);
  margin-right: var(--template-title-icon-gap);
  filter: brightness(0) invert(1);
}

.inner {
  padding: 0 var(--template-widget-inner-padding-x);
}

.widget[data-section-type='video'] .inner,
.widget[data-section-type='bottom-video'] .inner {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 0 var(--template-video-inner-padding-x) var(--template-video-inner-padding-bottom);
}

.summary {
  margin: 0 0 var(--space-2);
  color: var(--color-text-secondary);
  font-size: var(--font-size-base);
  white-space: pre-wrap;
}

.widget[data-section-type='intro'] .summary {
  font-family: var(--template-home-intro-summary-font);
  font-size: var(--template-home-intro-summary-size);
  line-height: var(--template-home-intro-summary-line-height);
  text-indent: 1em;
}
</style>
